import { renderDonutForPdf, renderBarForPdf, renderDualAxesForPdf } from "../pdfCharts";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";
import { getAnnualTotalsByGroup, buildBarChartData, buildBarChartDataCost, energyGroupLabels, ENERGY_GROUP_LABEL_KEYS } from "./TableGraf";
import { buildEnergyComparisonData, buildEnergyMonthlyData, buildSharedYScale } from "./energyChartData";

// "Enero" -> "Ene", "Septiembre" -> "Sep", etc. — nombres de mes en español
// truncan bien a 3 letras. Con los dos gráficos compartiendo una misma hoja
// (menos alto cada uno) el nombre completo no entra sin superponerse.
const monthLabel3 = (v) => String(v).slice(0, 3);

// Arma los 3 gráficos de TableGraf.js (dona de fuentes + barra de consumo +
// barra de costo) como SVG vectorial para el PDF — misma lógica de datos
// que ya usa TableGraf.js en pantalla, reutilizada tal cual.
export async function renderEnergyChartsForPdf(totales, filters, t) {
  const dataConsumo = getAnnualTotalsByGroup(totales, filters?.year);
  const donutData = Object.entries(dataConsumo)
    .filter(([, { total }]) => total > 0)
    .map(([groupKey, { total }]) => {
      const type = energyGroupLabels[groupKey];
      return { type, value: total, label: t(ENERGY_GROUP_LABEL_KEYS[type] || type) };
    });

  const [donut, barConsumo, barCosto] = await Promise.all([
    renderDonutForPdf(donutData, DONUT_COLORS.energy),
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya tienen las 2 secciones
    // DualAxes de más abajo.
    renderBarForPdf(buildBarChartData(totales), "MWh", BAR_PALETTE, { yFormatter: formatWhole }),
    renderBarForPdf(buildBarChartDataCost(totales), t("cost"), BAR_PALETTE, { yFormatter: formatWhole }),
  ]);

  return { donut, barConsumo, barCosto };
}

// "Energía Eléctrica Evolución de Consumos" — barras de consumo mensual por
// año + línea punteada de promedio, misma config que EnergyComparisonChart
// en EnergyKPIsDashBoard.js (sin tooltip/interacciones, no aplican a un PDF
// estático, ni el toggle de años — un PDF no es interactivo, van todos).
export async function renderComparisonForPdf(resultsByYear, t) {
  const { years, barData, lineData, yearColorMap } = buildEnergyComparisonData(resultsByYear, t);
  const sharedYScale = buildSharedYScale(barData, lineData);

  const children = [
    {
      data: barData,
      type: "interval",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 40 },
      scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
    },
    {
      data: lineData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: {
      color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) },
      // El eje X es una escala de banda (12 meses) para poder ubicar las
      // barras — por defecto reserva medio ancho de banda de margen a cada
      // lado para que la primera/última barra no queden pegadas al borde.
      // A este ancho (ahora que el gráfico ocupa toda la hoja) ese margen
      // por defecto se nota mucho más como espacio vacío a la derecha —
      // se achica sin eliminarlo del todo.
      x: { paddingOuter: 0.1 },
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: "kWh", grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

// "Energía Eléctrica Intensidad" — consumo/producción mensual (línea sólida)
// + promedio (línea punteada), misma config que EnergyMonthlyChart.
export async function renderMonthlyForPdf(resultsByYear, resultsProductions, t) {
  const { years, consumptionData, averageData, yearColorMap } = buildEnergyMonthlyData(resultsByYear, resultsProductions, t);
  const sharedYScale = buildSharedYScale(consumptionData, averageData);

  const children = [
    {
      data: consumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      connectNulls: false,
      style: { lineWidth: 4 },
      scale: { y: sharedYScale },
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
      connectNulls: false,
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
    axis: {
      x: { labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: `kWh/${t("unit")}`, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatMagnitudeAdaptive(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}
